import { PlatformSiteAri } from '@atlassian/ari';
import { createActivityClient } from '@atlassian/recent-work-client/activity-client';
import type { ActivityClient } from '@atlassian/recent-work-client/base';
import type { ActivityItem } from '@atlassian/recent-work-client/query';

import { postFetchFilterItemsByCloudIds } from './post-fetch-cloud-id-filter';
import { getWorkedOnAndViewedItemsResults } from './types';

const ACTIVITIES_LIMIT = 50;
const FETCH_ACTIVITIES_DEFAULT_RETRY_MS = [50, 125, 250];

export default class ActivityProvider {
	private activityClient: ActivityClient;

	constructor() {
		this.activityClient = createActivityClient('v3', 'atlas');
	}

	async getWorkedOnAndViewedItemsWithPostFiltering({
		cloudIds,
		collabContextId,
	}: {
		cloudIds?: string[];
		collabContextId?: PlatformSiteAri;
	}): Promise<getWorkedOnAndViewedItemsResults> {
		try {
			const activities = await this.activityClient.fetchActivities(
				FETCH_ACTIVITIES_DEFAULT_RETRY_MS,
				{ limit: ACTIVITIES_LIMIT, collabContextId: collabContextId?.toString() },
				['workedOn', 'viewed'],
			);

			const postFetchFilteredViewedItems = cloudIds
				? postFetchFilterItemsByCloudIds(activities.viewed, cloudIds)
				: activities.viewed;
			const postFetchFilteredWorkedOnItems = cloudIds
				? postFetchFilterItemsByCloudIds(activities.workedOn, cloudIds)
				: activities.workedOn;

			return {
				data: {
					viewed: postFetchFilteredViewedItems,
					workedOn: postFetchFilteredWorkedOnItems,
				},
				error: false,
			};
		} catch (error) {
			return {
				data: {
					viewed: [],
					workedOn: [],
				},
				error: true,
			};
		}
	}

	async getRecentItemsWithoutAtlasObjects(
		// Once "home-recents-root-container-ids" has rolled out, we can rely on collabContextId for skipping logic.
		// If collabContextId isn't set, we cannot call AP.
		userHasCloudId: boolean,
		collabContextId?: PlatformSiteAri,
	): Promise<ActivityItem[]> {
		if (!userHasCloudId) {
			return [];
		}

		const activities = await this.activityClient.fetchActivities(
			FETCH_ACTIVITIES_DEFAULT_RETRY_MS,
			{
				limit: ACTIVITIES_LIMIT,
				objectTypes: ['ISSUE', 'PAGE', 'BLOGPOST'],
				collabContextId: collabContextId?.toString(),
			},
			['viewed'],
		);

		return activities.viewed;
	}

	async searchFilteredRecent(
		search: string,
		// Once "home-recents-root-container-ids" has rolled out, we can rely on collabContextId for skipping logic.
		// If collabContextId isn't set, we cannot call AP.
		userHasCloudId: boolean,
		collabContextId?: PlatformSiteAri,
	): Promise<ActivityItem[]> {
		const items = await this.getRecentItemsWithoutAtlasObjects(userHasCloudId, collabContextId);
		return this.filterActivityItems(items, search);
	}

	private filterActivityItems(items: ActivityItem[], searchTerm: string) {
		if (searchTerm.length === 0) {
			return [];
		}

		searchTerm = searchTerm.toLowerCase();
		return items.filter((item) => {
			return Boolean(item.object.name?.toLowerCase().includes(searchTerm.toLowerCase()));
		});
	}
}
